/**
 * MyPayslips.js
 *
 * FIX: Form 16 now downloads directly as PDF instead of opening a new tab.
 *   - Replaced openForm16ForPrint() with forceDownload() helper
 *   - forceDownload() forces application/octet-stream so Chrome downloads
 *     instead of intercepting the PDF blob and opening it inline
 *   - Both "My Form 16" (employee) and per-employee Form 16 (HR) use the same fix
 *   - Payslip download also uses forceDownload for consistency
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Badge, Button, Spinner, Alert, Table,
    Form, Tabs, Tab,
} from 'react-bootstrap';
import {
    FileText, Download, IndianRupee, Calendar, Users, TrendingUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
const ADMIN_ROLES = ['admin','super-admin','hr-manager','account-manager'];
const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0, maximumFractionDigits: 2,
});
const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];
const currentFY = () => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(2)}`;
};
const fyOptions = () => {
    const d   = new Date();
    const cur = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return Array.from({ length: 5 }, (_, i) => {
        const y = cur - i;
        return `${y}-${String(y + 1).slice(2)}`;
    });
};
// ── FIX: force download helper ────────────────────────────────────────────────
// Chrome intercepts application/pdf blobs and opens them in its built-in PDF
// viewer (new tab) instead of downloading. Forcing the blob MIME type to
// application/octet-stream bypasses this — Chrome cannot sniff it as PDF
// and is forced to save it as a file download.
const forceDownload = (data, filename) => {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href          = url;
    link.download      = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 150);
};
// ── Generic axios blob fetch + forced download ────────────────────────────────
const fetchAndDownload = async (url, filename, onStart, onEnd) => {
    onStart?.();
    try {
        const res = await axiosInstance.get(url, { responseType: 'blob' });
        // Determine extension from Content-Type so filename is accurate
        const ct  = res.headers['content-type'] || '';
        let name  = filename;
        if (!name.includes('.')) {
            name += ct.includes('pdf') ? '.pdf' : '.html';
        }
        forceDownload(res.data, name);
    } catch (err) {
        try {
            const text = await new Response(err.response?.data).text();
            const msg  = JSON.parse(text)?.message;
            toast.error(msg || 'Download failed');
        } catch { toast.error('Download failed'); }
    } finally { onEnd?.(); }
};
// ── MyPayslips ────────────────────────────────────────────────────────────────
const MyPayslips = () => {
    const { user } = useAuth();
    const isHR     = ADMIN_ROLES.includes(user?.role);
    const [payslips,    setPayslips]    = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [fy,          setFY]          = useState(currentFY());
    const [form16List,  setForm16List]  = useState([]);
    const [f16Loading,  setF16Loading]  = useState(false);
    const [downloading, setDownloading] = useState(null);
    const loadPayslips = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/payroll/my-payslips');
            setPayslips(res.data.data || []);
        } catch { toast.error('Failed to load payslips'); }
        finally { setLoading(false); }
    };
    const loadForm16List = async () => {
        if (!isHR) return;
        setF16Loading(true);
        try {
            const res  = await axiosInstance.get(`/payroll/form16/list?financial_year=${fy}`);
            const data = res.data.data;
            setForm16List(Array.isArray(data) ? data : (data?.employees || []));
        } catch { toast.error('Failed to load Form 16 list'); }
        finally { setF16Loading(false); }
    };
    useEffect(() => { loadPayslips(); }, []);
    useEffect(() => { if (isHR) loadForm16List(); }, [fy, isHR]);
    // Payslip download
    const downloadPayslip = (id, label) => fetchAndDownload(
        `/payroll/download/${id}`,
        `Payslip_${label.replace(/\s/g,'_')}`,
        () => setDownloading(`payslip-${id}`),
        () => setDownloading(null)
    );
    // FIX: Form 16 — direct PDF download, no new tab
    const downloadMyForm16 = () => fetchAndDownload(
        `/payroll/form16/my?financial_year=${fy}`,
        `Form16_${fy.replace('-','_')}_${user?.first_name || 'Me'}_${user?.last_name || ''}`,
        () => setDownloading('form16-my'),
        () => setDownloading(null)
    );
    const downloadEmpForm16 = (empId, empName) => fetchAndDownload(
        `/payroll/form16/${empId}?financial_year=${fy}`,
        `Form16_${fy.replace('-','_')}_${empName.replace(/\s/g,'_')}`,
        () => setDownloading(`form16-${empId}`),
        () => setDownloading(null)
    );
    // Group payslips by year
    const grouped = payslips.reduce((acc, p) => {
        if (!acc[p.year]) acc[p.year] = [];
        acc[p.year].push(p);
        return acc;
    }, {});
    // FY summary for employee Form 16 tab
    const fyPayslipsSummary = () => {
        const [startY] = fy.split('-').map(Number);
        const endY     = startY + 1;
        const fySlips  = payslips.filter(p =>
            (p.month >= 4 && p.year === startY) || (p.month <= 3 && p.year === endY)
        );
        return {
            slips:      fySlips,
            totalGross: fySlips.reduce((s, p) => s + parseFloat(p.gross_salary || 0), 0),
            totalTDS:   fySlips.reduce((s, p) => s + parseFloat(p.tds_deduction || 0), 0),
            totalNet:   fySlips.reduce((s, p) => s + parseFloat(p.net_salary    || 0), 0),
        };
    };
    return (
        <div className="p-2 p-md-4">
            <div className="mb-4">
                <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                    <FileText size={22} className="text-primary"/>Payslips &amp; Form 16
                </h4>
                <p className="text-muted small mb-0">
                    View monthly payslips and download Form 16 for income tax filing
                </p>
            </div>
            <Tabs defaultActiveKey="payslips" className="mb-4">
                {/* ══ My Payslips Tab ══════════════════════════════════════ */}
                <Tab eventKey="payslips" title={`Payslips (${payslips.length})`}>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary"/>
                        </div>
                    ) : payslips.length === 0 ? (
                        <Alert variant="light" className="border text-center py-5">
                            <IndianRupee size={32} className="text-muted mb-2 d-block mx-auto"/>
                            <div className="text-muted">No payslips generated yet.</div>
                        </Alert>
                    ) : (
                        Object.entries(grouped).sort(([a],[b]) => b - a).map(([year, slips]) => (
                            <div key={year} className="mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <Calendar size={16} className="text-primary"/>
                                    <span className="fw-bold">{year}</span>
                                    <Badge bg="light" text="dark">{slips.length} payslips</Badge>
                                </div>
                                <Row className="g-3">
                                    {slips.map(p => (
                                        <Col key={p.id} xs={12} sm={6} md={4} xl={3}>
                                            <Card className="border-0 shadow-sm h-100"
                                                style={{ borderTop:'3px solid #4f46e5' }}>
                                                <Card.Body className="p-3">
                                                    <div className="d-flex justify-content-between mb-3">
                                                        <div>
                                                            <div className="fw-bold">
                                                                {MONTHS[p.month]} {p.year}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {p.company_name}
                                                            </div>
                                                        </div>
                                                        <Badge bg="success" style={{fontSize:10}}>
                                                            Processed
                                                        </Badge>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between small mb-1">
                                                            <span className="text-muted">Gross</span>
                                                            <span>₹{fmt(p.gross_salary)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between small mb-1">
                                                            <span className="text-muted">Deductions</span>
                                                            <span className="text-danger">
                                                                −₹{fmt(p.total_deductions)}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex justify-content-between fw-bold small border-top pt-1">
                                                            <span>Net Pay</span>
                                                            <span className="text-success">
                                                                ₹{fmt(p.net_salary)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <Button size="sm" variant="outline-primary"
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                        disabled={downloading === `payslip-${p.id}`}
                                                        onClick={() => downloadPayslip(
                                                            p.id,
                                                            `${MONTHS[p.month]}_${p.year}`
                                                        )}>
                                                        {downloading === `payslip-${p.id}`
                                                            ? <Spinner size="sm"/>
                                                            : <><Download size={13}/>Download Payslip</>}
                                                    </Button>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))
                    )}
                </Tab>
                {/* ══ Form 16 Tab ═══════════════════════════════════════════ */}
                <Tab eventKey="form16" title="Form 16">
                    <Alert variant="info" className="small mb-4">
                        <FileText size={14} className="me-2"/>
                        <strong>When is Form 16 available?</strong>
                        {' '}Form 16 is generated from your payslip data and can be downloaded
                        as soon as payroll is processed for any month in the financial year.
                        The <strong>final Form 16</strong> (complete FY) is available after
                        the <strong>March payroll</strong> is processed.
                    </Alert>
                    {/* FY selector + download button */}
                    <div className="d-flex align-items-end gap-3 mb-4 flex-wrap">
                        <div>
                            <Form.Label className="small fw-bold mb-1">Financial Year</Form.Label>
                            <Form.Select size="sm" style={{width:140}} value={fy}
                                onChange={e => setFY(e.target.value)}>
                                {fyOptions().map(f => (
                                    <option key={f} value={f}>{f}</option>
                                ))}
                            </Form.Select>
                        </div>
                        {/* FIX: direct PDF download, no new tab */}
                        <Button
                            variant="primary"
                            disabled={downloading === 'form16-my'}
                            onClick={downloadMyForm16}>
                            {downloading === 'form16-my'
                                ? <><Spinner size="sm" className="me-1"/>Downloading…</>
                                : <><Download size={14} className="me-1"/>Download My Form 16 — {fy}</>}
                        </Button>
                    </div>
                    {/* Employee: FY summary card */}
                    {!isHR && (() => {
                        const { slips, totalGross, totalTDS, totalNet } = fyPayslipsSummary();
                        if (!slips.length) return (
                            <Alert variant="light" className="border text-center py-4">
                                <div className="text-muted">
                                    No payslips found for FY {fy}.
                                    Form 16 will be available once payroll is processed.
                                </div>
                            </Alert>
                        );
                        return (
                            <Card className="border-0 shadow-sm p-4">
                                <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                    <TrendingUp size={16} className="text-primary"/>
                                    FY {fy} — Summary ({slips.length} months processed)
                                </div>
                                <Row className="g-3">
                                    {[
                                        { label:'Months Paid',    val:`${slips.length}`,     color:'#4f46e5' },
                                        { label:'Annual Gross',   val:`₹${fmt(totalGross)}`, color:'#059669' },
                                        { label:'Total TDS',      val:`₹${fmt(totalTDS)}`,  color:'#dc2626' },
                                        { label:'Annual Net Pay', val:`₹${fmt(totalNet)}`,  color:'#0891b2' },
                                    ].map((k, i) => (
                                        <Col key={i} xs={6} md={3}>
                                            <div className="text-center p-3 rounded"
                                                style={{background:'#f8fafc'}}>
                                                <div style={{fontSize:22,fontWeight:800,color:k.color}}>
                                                    {k.val}
                                                </div>
                                                <div className="text-muted small">{k.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card>
                        );
                    })()}
                    {/* HR: all-employee grid */}
                    {isHR && (
                        f16Loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary"/>
                                <p className="mt-2 text-muted small">Loading employee list…</p>
                            </div>
                        ) : form16List.length === 0 ? (
                            <Alert variant="light" className="border text-center py-5">
                                <Users size={32} className="text-muted mb-2 d-block mx-auto"/>
                                <div className="text-muted">No payroll data for FY {fy}.</div>
                                <div className="text-muted small mt-1">
                                    Run payroll for at least one month in this financial year.
                                </div>
                            </Alert>
                        ) : (
                            <Card className="border-0 shadow-sm">
                                <div className="p-3 border-bottom d-flex align-items-center gap-2">
                                    <Users size={16} className="text-primary"/>
                                    <span className="fw-bold">{form16List.length} employees</span>
                                    <span className="text-muted small">— FY {fy}</span>
                                </div>
                                <Table hover responsive className="align-middle small mb-0">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                            <th className="ps-3">Employee</th>
                                            <th className="text-end">Months</th>
                                            <th className="text-end">Total Gross (₹)</th>
                                            <th className="text-end">Total TDS (₹)</th>
                                            <th>PAN</th>
                                            <th className="pe-3 text-end">Form 16</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form16List.map(emp => (
                                            <tr key={emp.id}>
                                                <td className="ps-3">
                                                    <div className="fw-medium">
                                                        {emp.first_name} {emp.last_name}
                                                    </div>
                                                    <div className="text-muted" style={{fontSize:10}}>
                                                        {emp.employee_code}
                                                    </div>
                                                </td>
                                                <td className="text-end">{emp.payslip_count}</td>
                                                <td className="text-end">₹{fmt(emp.total_gross)}</td>
                                                <td className="text-end text-danger">₹{fmt(emp.total_tds)}</td>
                                                <td>
                                                    {emp.pan_number
                                                        ? <span style={{fontFamily:'monospace',fontSize:11}}>{emp.pan_number}</span>
                                                        : <Badge bg="danger" style={{fontSize:9}}>NOT SET</Badge>
                                                    }
                                                </td>
                                                <td className="pe-3 text-end">
                                                    {/* FIX: direct PDF download */}
                                                    <Button size="sm" variant="outline-primary"
                                                        className="py-0"
                                                        disabled={downloading === `form16-${emp.id}`}
                                                        onClick={() => downloadEmpForm16(
                                                            emp.id,
                                                            `${emp.first_name} ${emp.last_name}`
                                                        )}>
                                                        {downloading === `form16-${emp.id}`
                                                            ? <Spinner size="sm"/>
                                                            : <><Download size={12}/> Form 16</>}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card>
                        )
                    )}
                </Tab>
            </Tabs>
        </div>
    );
};
export default MyPayslips;
